/**
 * Where a crafting ingredient comes from (Tome UI Round 4, the audit's "ingredient/source links"):
 * the vendors that sell it and the mobs that drop it, read from the authored content the client
 * already ships (NPC shop lists, loot tables, zone spawns). Each source names its atlas place as a
 * link that opens the World Atlas there; a place the atlas does not show is left unnamed. Nothing
 * here asks the server or predicts a drop: it is a reading aid next to the server-owned recipe.
 */
import React, { useMemo } from 'react';
import { MONSTER_DROPS } from '../../../../shared/ItemData';
import { MOB_CONFIG, NPC_CONFIG } from '../../../../shared/content/generated/entities.gen';
import { ZONE_CONFIGS } from '../../../../shared/content/generated/zones.gen';
import { atlasPlace, npcAtlasPlace, showOnAtlas, type AtlasPlace } from '../WorldMap/atlasFocus';

export interface CraftingSource { kind: 'vendor' | 'drop'; id: string; name: string; place: AtlasPlace | null }

const SHOWN_PER_KIND = 2;

function dropSources(itemId: string): CraftingSource[] {
  const found: (CraftingSource & { level: number })[] = [];
  for (const [mobId, table] of Object.entries(MONSTER_DROPS)) {
    const drops = [...(table.common ?? []), ...(table.uncommon ?? []), ...(table.rare ?? [])];
    const mob = MOB_CONFIG[mobId];
    if (!mob || !drops.some(drop => drop.itemId === itemId && drop.dropRate > 0)) continue;
    const zones = Object.keys(ZONE_CONFIGS).filter(zoneId => ZONE_CONFIGS[zoneId].spawns.some(spawn => spawn.mobId === mobId));
    if (!zones.length) continue; // a loot table for a mob no zone places is not somewhere to go
    const place = zones.map(atlasPlace).find(Boolean) ?? null;
    if (found.some(source => source.name === mob.name && source.place?.mapId === place?.mapId)) continue;
    found.push({ kind: 'drop', id: mobId, name: mob.name, place, level: mob.level });
  }
  return found
    .sort((a, b) => Number(!a.place) - Number(!b.place) || a.level - b.level || a.name.localeCompare(b.name))
    .map(({ level: _level, ...source }) => source);
}

function vendorSources(itemId: string): CraftingSource[] {
  const found: CraftingSource[] = [];
  for (const npc of Object.values(NPC_CONFIG)) {
    if (!npc.shopItems?.includes(itemId)) continue;
    if (!Object.values(ZONE_CONFIGS).some(zone => zone.npcSpawns.some(spawn => spawn.npcId === npc.id))) continue;
    found.push({ kind: 'vendor', id: npc.id, name: npc.name, place: npcAtlasPlace(npc.id) });
  }
  return found.sort((a, b) => Number(!a.place) - Number(!b.place) || a.name.localeCompare(b.name));
}

/** Vendors first (the surest source), then drops; both nearest-to-the-atlas first. */
export function craftingSources(itemId: string): CraftingSource[] {
  return [...vendorSources(itemId), ...dropSources(itemId)];
}

const SourceName: React.FC<{ source: CraftingSource }> = ({ source }) => <span className="crafting-tome__source"
  data-source-kind={source.kind} data-source-id={source.id}>
  {source.name}
  {source.place && <> in <button type="button" className="crafting-tome__place" title="Show on the World Atlas"
    aria-label={`Show ${source.place.name} on the World Atlas`}
    onClick={() => showOnAtlas(source.place!.mapId)}>{source.place.name}</button></>}
</span>;

const SourceRow: React.FC<{ label: string; sources: CraftingSource[] }> = ({ label, sources }) => {
  if (!sources.length) return null;
  const shown = sources.slice(0, SHOWN_PER_KIND);
  const more = sources.length - shown.length;
  return <div><dt>{label}</dt><dd>
    {shown.map((source, index) => <React.Fragment key={`${source.id}-${source.place?.mapId ?? 'none'}`}>
      {index > 0 && <span aria-hidden="true">{' · '}</span>}<SourceName source={source} />
    </React.Fragment>)}
    {more > 0 && <span className="crafting-tome__source-more"> · and {more} more</span>}
  </dd></div>;
};

/** "Sold by … / Dropped by …" under one ingredient, or an honest "No known source yet." */
export const CraftingSourceLinks: React.FC<{ itemId: string }> = ({ itemId }) => {
  const sources = useMemo(() => craftingSources(itemId), [itemId]);
  if (!sources.length) return <p className="crafting-tome__sources crafting-tome__sources--none">No known source yet.</p>;
  return <dl className="crafting-tome__sources">
    <SourceRow label="Sold by" sources={sources.filter(source => source.kind === 'vendor')} />
    <SourceRow label="Dropped by" sources={sources.filter(source => source.kind === 'drop')} />
  </dl>;
};
